import React, { useCallback, useEffect, useRef, useState } from "react"
import Button from "react-bootstrap/Button"
import { GoogleMap, useLoadScript } from "@react-google-maps/api"
import {
  defaultLat,
  defaultLng,
  googleAPIKeys,
  GOOGLE_MAPS_LIBRARIES,
} from "common/utils"
import {
  findOverlappingBoundary,
  isPointInsideExistingZones,
  pointsToGeoJson,
  toLatLngLiteral,
  toLatLngPaths,
} from "common/geoBoundaryUtils"
import { useTranslation } from "react-i18next"

const mapContainerStyle = { width: "100%", height: "400px" }

/** Average of path literals — never pass Google LatLng into Marker/Bounds. */
const getPathsCentroidLiteral = paths => {
  if (!paths?.length) return null

  let latSum = 0
  let lngSum = 0
  let count = 0

  paths.forEach(point => {
    const literal = toLatLngLiteral(point)
    if (!literal) return
    latSum += literal.lat
    lngSum += literal.lng
    count += 1
  })

  if (!count) return null
  return { lat: latSum / count, lng: lngSum / count }
}

const createZoneNameMarker = (map, paths, label, color) => {
  const text = String(label || "").trim()
  if (!map || !text || !paths?.length || !window.google?.maps) return null

  const position = getPathsCentroidLiteral(paths)
  if (!position) return null

  return new window.google.maps.Marker({
    position,
    map,
    clickable: false,
    icon: {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 0,
    },
    label: {
      text,
      color,
      fontSize: "13px",
      fontWeight: "700",
    },
  })
}

const pathToGeoJson = path => {
  const coords = path
    .getArray()
    .map(latLng => {
      const literal = toLatLngLiteral(latLng)
      return literal ? [literal.lng, literal.lat] : null
    })
    .filter(Boolean)
  if (coords.length < 3) return null

  const first = coords[0]
  const last = coords[coords.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coords.push([...first])
  }

  return { type: "Polygon", coordinates: [coords] }
}

const RegionBoundaryPicker = ({
  boundary,
  onBoundaryChange,
  existingBoundaries = [],
  excludeZoneId = null,
  zonesReady = true,
  currentZoneLabel = "",
}) => {
  const { t, i18n } = useTranslation()
  const mapRef = useRef(null)
  const polygonRef = useRef(null)
  const polylineRef = useRef(null)
  const mapClickListenerRef = useRef(null)
  const pathListenersRef = useRef([])
  const existingPolygonsRef = useRef([])
  const existingZoneLabelsRef = useRef([])
  const currentZoneLabelMarkerRef = useRef(null)
  const skipExternalSyncRef = useRef(false)
  const drawingPointsRef = useRef([])
  const lastValidBoundaryRef = useRef(boundary)
  const existingBoundariesRef = useRef(existingBoundaries)
  const excludeZoneIdRef = useRef(excludeZoneId)
  const [isDrawing, setIsDrawing] = useState(false)
  const [pointCount, setPointCount] = useState(0)
  const [overlapError, setOverlapError] = useState(null)

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleAPIKeys,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  existingBoundariesRef.current = existingBoundaries
  excludeZoneIdRef.current = excludeZoneId

  const clearPathListeners = () => {
    pathListenersRef.current.forEach(listener =>
      window.google.maps.event.removeListener(listener)
    )
    pathListenersRef.current = []
  }

  const emitBoundary = useCallback(
    geoJson => {
      skipExternalSyncRef.current = true
      onBoundaryChange(geoJson)
    },
    [onBoundaryChange]
  )

  const getZoneLabel = useCallback(
    zone =>
      i18n.language === "sa"
        ? zone?.nameAr || zone?.name
        : zone?.name || zone?.nameAr,
    [i18n.language]
  )

  const getZoneLabelRef = useRef(getZoneLabel)
  getZoneLabelRef.current = getZoneLabel
  const currentZoneLabelRef = useRef(currentZoneLabel)
  currentZoneLabelRef.current = currentZoneLabel

  const clearExistingPolygons = useCallback(() => {
    existingPolygonsRef.current.forEach(polygon => polygon.setMap(null))
    existingPolygonsRef.current = []
    existingZoneLabelsRef.current.forEach(marker => marker.setMap(null))
    existingZoneLabelsRef.current = []
  }, [])

  const clearCurrentZoneLabel = useCallback(() => {
    if (currentZoneLabelMarkerRef.current) {
      currentZoneLabelMarkerRef.current.setMap(null)
      currentZoneLabelMarkerRef.current = null
    }
  }, [])

  const updateCurrentZoneLabel = useCallback(
    (map, geoJson, label) => {
      clearCurrentZoneLabel()
      if (!map || !geoJson?.coordinates?.[0]?.length) return

      const paths = toLatLngPaths(geoJson.coordinates[0])
      if (paths.length < 3) return
      const marker = createZoneNameMarker(map, paths, label, "#1565c0")
      if (marker) {
        currentZoneLabelMarkerRef.current = marker
      }
    },
    [clearCurrentZoneLabel]
  )

  const showExistingBoundaries = useCallback(
    map => {
      clearExistingPolygons()
      if (!map) return

      existingBoundaries.forEach(zone => {
        if (excludeZoneId && String(zone._id) === String(excludeZoneId)) {
          return
        }
        if (!zone.boundary?.coordinates?.[0]?.length) return

        const paths = toLatLngPaths(zone.boundary.coordinates[0])
        if (paths.length < 3) return
        const polygon = new window.google.maps.Polygon({
          paths,
          editable: false,
          draggable: false,
          clickable: false,
          map,
          strokeColor: "#d32f2f",
          strokeOpacity: 0.85,
          strokeWeight: 2,
          fillColor: "#d32f2f",
          fillOpacity: 0.12,
        })
        existingPolygonsRef.current.push(polygon)

        const labelMarker = createZoneNameMarker(
          map,
          paths,
          getZoneLabel(zone),
          "#c62828"
        )
        if (labelMarker) {
          existingZoneLabelsRef.current.push(labelMarker)
        }
      })
    },
    [clearExistingPolygons, existingBoundaries, excludeZoneId, getZoneLabel]
  )

  const detectOverlap = useCallback(geoJson => {
    return findOverlappingBoundary(
      geoJson,
      existingBoundariesRef.current,
      excludeZoneIdRef.current
    )
  }, [])

  const tryEmitBoundaryRef = useRef(() => true)

  const showPolygonWithListeners = useCallback(
    (map, geoJson, label = currentZoneLabel) => {
      if (!geoJson?.coordinates?.[0]?.length) return

      if (polygonRef.current) {
        polygonRef.current.setMap(null)
        polygonRef.current = null
      }
      clearPathListeners()

      const paths = toLatLngPaths(geoJson.coordinates[0])
      if (paths.length < 3) return
      polygonRef.current = new window.google.maps.Polygon({
        paths,
        editable: true,
        draggable: false,
        map,
        strokeColor: "#1976d2",
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: "#1976d2",
        fillOpacity: 0.2,
      })

      const path = polygonRef.current.getPath()
      clearPathListeners()
      pathListenersRef.current = ["set_at", "insert_at", "remove_at"].map(event =>
        window.google.maps.event.addListener(path, event, () =>
          tryEmitBoundaryRef.current(pathToGeoJson(path))
        )
      )

      updateCurrentZoneLabel(map, geoJson, label)

      const bounds = new window.google.maps.LatLngBounds()
      paths.forEach(point => {
        const literal = toLatLngLiteral(point)
        if (literal) bounds.extend(literal)
      })
      map.fitBounds(bounds)
    },
    [currentZoneLabel, updateCurrentZoneLabel]
  )

  const revertToLastValidBoundary = useCallback(() => {
    const map = mapRef.current
    if (!map) return

    if (lastValidBoundaryRef.current?.coordinates?.[0]?.length) {
      showPolygonWithListeners(map, lastValidBoundaryRef.current)
      return
    }

    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
    }
    clearPathListeners()
  }, [showPolygonWithListeners])

  const tryEmitBoundary = useCallback(
    geoJson => {
      if (!geoJson) {
        setOverlapError(null)
        lastValidBoundaryRef.current = null
        clearCurrentZoneLabel()
        emitBoundary(null)
        return true
      }

      const overlappingZone = detectOverlap(geoJson)

      if (overlappingZone) {
        setOverlapError(getZoneLabel(overlappingZone))
        revertToLastValidBoundary()
        return false
      }

      setOverlapError(null)
      lastValidBoundaryRef.current = geoJson
      emitBoundary(geoJson)
      if (mapRef.current) {
        updateCurrentZoneLabel(
          mapRef.current,
          geoJson,
          currentZoneLabelRef.current
        )
      }
      return true
    },
    [
      clearCurrentZoneLabel,
      detectOverlap,
      emitBoundary,
      getZoneLabel,
      revertToLastValidBoundary,
      updateCurrentZoneLabel,
    ]
  )

  tryEmitBoundaryRef.current = tryEmitBoundary

  const attachPathListeners = useCallback(path => {
    clearPathListeners()
    pathListenersRef.current = ["set_at", "insert_at", "remove_at"].map(event =>
      window.google.maps.event.addListener(path, event, () =>
        tryEmitBoundaryRef.current(pathToGeoJson(path))
      )
    )
  }, [])

  const clearDrawingOverlays = useCallback(() => {
    if (polylineRef.current) {
      polylineRef.current.setMap(null)
      polylineRef.current = null
    }
    drawingPointsRef.current = []
    setPointCount(0)
  }, [])

  const stopDrawing = useCallback(() => {
    setIsDrawing(false)
    if (mapClickListenerRef.current) {
      window.google.maps.event.removeListener(mapClickListenerRef.current)
      mapClickListenerRef.current = null
    }
  }, [])

  const validateDrawingPoint = useCallback(
    (latLng, nextPoints) => {
      if (!zonesReady) {
        return {
          valid: false,
          message: t("Loading existing zones. Please wait before drawing."),
        }
      }

      const insideZone = isPointInsideExistingZones(
        latLng,
        existingBoundariesRef.current,
        excludeZoneIdRef.current
      )
      if (insideZone) {
        return {
          valid: false,
          message: t(
            'Cannot place points inside zone "{{name}}". Draw outside existing boundaries.',
            { name: getZoneLabelRef.current(insideZone) }
          ),
        }
      }

      if (nextPoints.length >= 3) {
        const tentativeBoundary = pointsToGeoJson(nextPoints)
        const overlappingZone = findOverlappingBoundary(
          tentativeBoundary,
          existingBoundariesRef.current,
          excludeZoneIdRef.current
        )
        if (overlappingZone) {
          return {
            valid: false,
            message: t(
              'This shape overlaps zone "{{name}}". Choose a different area.',
              { name: getZoneLabelRef.current(overlappingZone) }
            ),
          }
        }
      }

      return { valid: true }
    },
    [t, zonesReady]
  )

  const startDrawing = useCallback(() => {
    const map = mapRef.current
    if (!map || !zonesReady) return

    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
      clearPathListeners()
      tryEmitBoundary(null)
    }
    clearDrawingOverlays()
    setOverlapError(null)
    setIsDrawing(true)

    polylineRef.current = new window.google.maps.Polyline({
      map,
      path: [],
      clickable: false,
      strokeColor: "#1976d2",
      strokeWeight: 2,
    })

    mapClickListenerRef.current = window.google.maps.event.addListener(
      map,
      "click",
      event => {
        const point = { lat: event.latLng.lat(), lng: event.latLng.lng() }
        const nextPoints = [...drawingPointsRef.current, point]
        const validation = validateDrawingPoint(event.latLng, nextPoints)

        if (!validation.valid) {
          setOverlapError(validation.message)
          return
        }

        setOverlapError(null)
        drawingPointsRef.current = nextPoints
        setPointCount(nextPoints.length)
        polylineRef.current.setPath(nextPoints)
      }
    )
  }, [clearDrawingOverlays, tryEmitBoundary, validateDrawingPoint, zonesReady])

  const finishDrawing = useCallback(() => {
    if (!mapRef.current || drawingPointsRef.current.length < 3) return

    const geoJson = pointsToGeoJson(drawingPointsRef.current)
    const overlappingZone = detectOverlap(geoJson)

    if (overlappingZone) {
      setOverlapError(
        t(
          'This boundary overlaps with zone "{{name}}". Adjust it so zones do not overlap.',
          { name: getZoneLabel(overlappingZone) }
        )
      )
      return
    }

    stopDrawing()

    if (polylineRef.current) {
      polylineRef.current.setMap(null)
      polylineRef.current = null
    }
    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
      clearPathListeners()
    }

    polygonRef.current = new window.google.maps.Polygon({
      paths: drawingPointsRef.current,
      editable: true,
      draggable: false,
      map: mapRef.current,
      strokeColor: "#1976d2",
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: "#1976d2",
      fillOpacity: 0.2,
    })

    setOverlapError(null)
    lastValidBoundaryRef.current = geoJson
    emitBoundary(geoJson)
    attachPathListeners(polygonRef.current.getPath())
    updateCurrentZoneLabel(mapRef.current, geoJson, currentZoneLabel)
  }, [
    attachPathListeners,
    currentZoneLabel,
    detectOverlap,
    emitBoundary,
    getZoneLabel,
    stopDrawing,
    t,
    updateCurrentZoneLabel,
  ])

  const handleMapLoad = useCallback(
    map => {
      mapRef.current = map
      showExistingBoundaries(map)
      if (boundary?.coordinates?.[0]?.length) {
        lastValidBoundaryRef.current = boundary
        showPolygonWithListeners(map, boundary)
      }
    },
    [boundary, showExistingBoundaries, showPolygonWithListeners]
  )

  useEffect(() => {
    if (!mapRef.current || skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false
      return
    }
    if (boundary?.coordinates?.[0]?.length) {
      lastValidBoundaryRef.current = boundary
      showPolygonWithListeners(mapRef.current, boundary)
    }
  }, [boundary, showPolygonWithListeners])

  useEffect(() => {
    if (mapRef.current && lastValidBoundaryRef.current?.coordinates?.[0]?.length) {
      updateCurrentZoneLabel(
        mapRef.current,
        lastValidBoundaryRef.current,
        currentZoneLabel
      )
    }
  }, [currentZoneLabel, updateCurrentZoneLabel])

  useEffect(() => {
    if (mapRef.current) {
      showExistingBoundaries(mapRef.current)
    }
  }, [showExistingBoundaries])

  useEffect(() => {
    if (!zonesReady || !boundary?.coordinates?.[0]?.length) return

    const overlappingZone = detectOverlap(boundary)
    if (!overlappingZone) return

    setOverlapError(getZoneLabel(overlappingZone))
    lastValidBoundaryRef.current = null

    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
    }
    clearPathListeners()
    clearCurrentZoneLabel()
    emitBoundary(null)
  }, [boundary, detectOverlap, emitBoundary, getZoneLabel, zonesReady, clearCurrentZoneLabel])

  const handleClear = () => {
    stopDrawing()
    if (polygonRef.current) {
      polygonRef.current.setMap(null)
      polygonRef.current = null
    }
    clearDrawingOverlays()
    clearPathListeners()
    setOverlapError(null)
    tryEmitBoundary(null)
  }

  useEffect(() => {
    return () => {
      stopDrawing()
      clearDrawingOverlays()
      clearPathListeners()
      clearExistingPolygons()
      clearCurrentZoneLabel()
      if (polygonRef.current) {
        polygonRef.current.setMap(null)
        polygonRef.current = null
      }
    }
  }, [clearCurrentZoneLabel, clearDrawingOverlays, clearExistingPolygons, stopDrawing])

  return (
    <div>
      <p className="text-muted small mb-2">
        {t(
          "Draw the delivery boundary on the map. Existing zones are shown in red with their names and must not overlap."
        )}
      </p>
      {!zonesReady && (
        <p className="text-muted small mb-2">{t("Loading existing zones...")}</p>
      )}
      <div style={mapContainerStyle}>
        {!isLoaded ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            {t("Loading")}...
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={{ lat: defaultLat, lng: defaultLng }}
            zoom={10}
            onLoad={handleMapLoad}
          />
        )}
      </div>
      <div className="mt-2">
        <Button
          variant={isDrawing ? "secondary" : "outline-primary"}
          size="sm"
          className="me-2"
          onClick={startDrawing}
          disabled={!zonesReady || !isLoaded}
        >
          {t("Start Boundary")}
        </Button>
        <Button
          variant="outline-success"
          size="sm"
          className="me-2"
          onClick={finishDrawing}
          disabled={!isDrawing || pointCount < 3 || !zonesReady}
        >
          {t("Finish Boundary")}
        </Button>
        <Button variant="outline-danger" size="sm" onClick={handleClear}>
          {t("Clear Boundary")}
        </Button>
      </div>
      {isDrawing && (
        <p className="text-muted small mb-0 mt-2">
          {t(
            "Click on the map to add points. Points inside existing zones are blocked."
          )}
        </p>
      )}
      {overlapError && (
        <p className="text-danger small mb-0 mt-2">{overlapError}</p>
      )}
    </div>
  )
}

export default RegionBoundaryPicker
